import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowsLeftRight, ArrowSquareOut, ArrowsClockwise, SignIn, SignOut, Swap, Star } from "@phosphor-icons/react";
import { timingTransfers, timingTransferMark } from "../../lib/timing-api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { track } from "../../lib/analytics";

const L = {
    cs: {
        title: "Potvrzené přestupy · letní okno 2026",
        subtitle: "Jen dokončené přestupy klubů PL 2026/27 — včetně odchodů mimo ligu. Zdroje: SportMonks API + BBC done deals.",
        in: "Příchody", out: "Odchody", internal: "V rámci PL", loans: "Hostování",
        all: "Vše", allTeams: "Všechny týmy", hideLoans: "Skrýt hostování",
        loading: "Načítám přestupy…", refreshing: "Aktualizuji data z SportMonks + BBC…",
        refresh: "Aktualizovat", empty: "Žádné přestupy neodpovídají filtrům.",
        date: "Datum", player: "Hráč", move: "Přestup", type: "Typ", fee: "Částka", sources: "Zdroje",
        outOfPl: "MIMO PL", transfer: "PŘESTUP", loan: "HOSTOVÁNÍ",
        updated: "Aktualizováno", inDb: "V DB",
    },
    en: {
        title: "Confirmed transfers · summer window 2026",
        subtitle: "Completed deals only for 2026/27 PL clubs — incl. departures out of the league. Sources: SportMonks API + BBC done deals.",
        in: "Incoming", out: "Departures", internal: "Within PL", loans: "Loans",
        all: "All", allTeams: "All teams", hideLoans: "Hide loans",
        loading: "Loading transfers…", refreshing: "Refreshing from SportMonks + BBC…",
        refresh: "Refresh", empty: "No transfers match the filters.",
        date: "Date", player: "Player", move: "Move", type: "Type", fee: "Fee", sources: "Sources",
        outOfPl: "OUT OF PL", transfer: "TRANSFER", loan: "LOAN",
        updated: "Updated", inDb: "In DB",
    },
};

const fmtAmount = (eur) => {
    if (!eur) return null;
    if (eur >= 1_000_000) return `€${(eur / 1_000_000).toFixed(eur % 1_000_000 ? 1 : 0)}M`;
    return `€${Math.round(eur / 1000)}k`;
};

const DIR_STYLE = {
    in: { cls: "bg-fa-mint/15 text-fa-mint border-fa-mint/40", icon: SignIn },
    out: { cls: "bg-red-500/15 text-red-400 border-red-500/40", icon: SignOut },
    internal: { cls: "bg-fa-sky/15 text-fa-sky border-fa-sky/40", icon: Swap },
};

export default function TransfersBoard({ lang = "cs" }) {
    const s = L[lang] || L.cs;
    const { user } = useAuth();
    const isAdmin = !!user?.is_admin;
    const [data, setData] = useState(null);
    const [dir, setDir] = useState("all");
    const [team, setTeam] = useState("all");
    const [hideLoans, setHideLoans] = useState(false);
    const [importantOnly, setImportantOnly] = useState(false);
    const pollRef = useRef(null);

    const toggleMark = async (row) => {
        if (!isAdmin) return;
        try {
            const next = !row.important;
            await timingTransferMark(row.row_id, next);
            setData((d) => ({
                ...d,
                transfers: d.transfers.map((r) =>
                    r.row_id === row.row_id ? { ...r, important: next, manual_mark: true } : r),
            }));
            track("transfers_mark", { tool: "timing", meta: { row_id: row.row_id, important: next } });
        } catch {
            toast.error("Označení se nepodařilo uložit");
        }
    };

    const load = async (refresh = false) => {
        try {
            const d = await timingTransfers({ refresh });
            setData(d);
            if (d?.meta?.status === "running" && !pollRef.current) {
                pollRef.current = setInterval(async () => {
                    try {
                        const d2 = await timingTransfers();
                        setData(d2);
                        if (d2?.meta?.status !== "running") {
                            clearInterval(pollRef.current);
                            pollRef.current = null;
                        }
                    } catch { /* ignore */ }
                }, 5000);
            }
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
        track("transfers_view", { tool: "timing" });
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rows = data?.transfers || [];
    const teams = useMemo(() => {
        const set = new Set(rows.map((r) => r.epl_team).filter(Boolean));
        return [...set].sort();
    }, [rows]);

    const filtered = rows.filter((r) => {
        if (importantOnly && !r.important) return false;
        if (dir !== "all" && r.direction !== dir) return false;
        if (team !== "all" && r.epl_team !== team) return false;
        if (hideLoans && r.transfer_type === "loan") return false;
        return true;
    });

    const importantCount = rows.filter((r) => r.important).length;

    const counts = data?.meta?.counts;
    const running = data?.meta?.status === "running";

    return (
        <div className="space-y-4" data-testid="transfers-board">
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
                            <ArrowsLeftRight size={16} weight="fill" className="text-fa-sky" />
                            {s.title}
                        </h2>
                        <p className="font-mono text-[10px] text-white/50 mt-1 max-w-2xl leading-relaxed">{s.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {data?.meta?.fetched_at && (
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                {s.updated}: {new Date(data.meta.fetched_at).toLocaleString(lang === "cs" ? "cs" : "en-GB")}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => load(true)}
                            disabled={running}
                            data-testid="transfers-refresh-btn"
                            className="font-mono text-[10px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black disabled:opacity-50 flex items-center gap-1"
                        >
                            <ArrowsClockwise size={11} className={running ? "animate-spin" : ""} />
                            {s.refresh}
                        </button>
                    </div>
                </div>

                {counts && (
                    <div className="mt-3 flex gap-2 flex-wrap" data-testid="transfers-summary">
                        {[["in", s.in, counts.in], ["out", s.out, counts.out], ["internal", s.internal, counts.internal]].map(([k, label, n]) => {
                            const st = DIR_STYLE[k];
                            const Icon = st.icon;
                            return (
                                <span key={k} className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border flex items-center gap-1.5 ${st.cls}`}>
                                    <Icon size={11} weight="fill" /> {label}: <strong className="tabular-nums">{n}</strong>
                                </span>
                            );
                        })}
                        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-white/15 text-white/60">
                            {s.loans}: <strong className="tabular-nums">{counts.loans}</strong>
                        </span>
                    </div>
                )}
            </header>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="transfers-filters">
                <button
                    type="button"
                    onClick={() => setImportantOnly(!importantOnly)}
                    data-testid="transfers-important-filter"
                    title="Hráči známí z FPL historie, přestupy ≥ €15M a ručně označené adminem"
                    className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border flex items-center gap-1 ${importantOnly ? "bg-fa-yellow text-black border-fa-yellow font-bold" : "border-fa-yellow/40 text-fa-yellow hover:bg-fa-yellow/10"}`}
                >
                    <Star size={11} weight="fill" />
                    Důležité pro FPL ({importantCount})
                </button>
                {[["all", s.all], ["in", s.in], ["out", s.out], ["internal", s.internal]].map(([k, label]) => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setDir(k)}
                        data-testid={`transfers-dir-${k}`}
                        className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${dir === k ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                    >
                        {label}
                    </button>
                ))}
                <select
                    value={team}
                    onChange={(e) => setTeam(e.target.value)}
                    data-testid="transfers-team-filter"
                    className="font-mono text-[10px] uppercase tracking-widest bg-[#0d0d0d] border border-white/15 text-white/70 px-2 py-1.5 outline-none"
                >
                    <option value="all">{s.allTeams}</option>
                    {teams.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5 cursor-pointer select-none">
                    <input type="checkbox" checked={hideLoans} onChange={(e) => setHideLoans(e.target.checked)}
                           data-testid="transfers-hide-loans" className="accent-[#38bdf8]" />
                    {s.hideLoans}
                </label>
                <span className="ml-auto font-mono text-[10px] text-white/40 tabular-nums">{filtered.length} / {rows.length}</span>
            </div>

            {/* Loading / refreshing state */}
            {running && (
                <div className="border border-fa-sky/30 bg-fa-sky/5 p-3 font-mono text-[11px] text-fa-sky flex items-center gap-2"
                     data-testid="transfers-refreshing">
                    <ArrowsClockwise size={13} className="animate-spin" /> {rows.length ? s.refreshing : s.loading}
                </div>
            )}

            {/* Table */}
            {filtered.length > 0 && (
                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                    <table className="w-full text-left" data-testid="transfers-table">
                        <thead>
                            <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                                <th className="px-3 py-2">{s.date}</th>
                                <th className="px-3 py-2">{s.player}</th>
                                <th className="px-3 py-2">{s.move}</th>
                                <th className="px-3 py-2">{s.type}</th>
                                <th className="px-3 py-2 text-right">{s.fee}</th>
                                <th className="px-3 py-2 text-right">{s.sources}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const st = DIR_STYLE[r.direction] || DIR_STYLE.internal;
                                const Icon = st.icon;
                                return (
                                    <tr key={r.row_id} className="border-b border-white/5 hover:bg-white/[0.03]"
                                        data-testid={`transfers-row-${r.row_id}`}>
                                        <td className="px-3 py-2 font-mono text-[10px] text-white/50 tabular-nums whitespace-nowrap">{r.date || "—"}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleMark(r)}
                                                    disabled={!isAdmin}
                                                    data-testid={`transfers-star-${r.row_id}`}
                                                    title={isAdmin
                                                        ? (r.important ? "Odznačit (admin)" : "Označit jako důležité pro FPL (admin)")
                                                        : (r.important ? "Důležité pro FPL" : "")}
                                                    className={`${isAdmin ? "cursor-pointer hover:scale-125 transition-transform" : "cursor-default"} ${r.important ? "text-fa-yellow" : isAdmin ? "text-white/20 hover:text-fa-yellow/60" : "text-transparent"}`}
                                                >
                                                    <Star size={13} weight={r.important ? "fill" : "regular"} />
                                                </button>
                                                {r.player_image && (
                                                    <img src={r.player_image} alt="" className="w-6 h-6 rounded-full object-cover bg-white/10" loading="lazy" />
                                                )}
                                                <span className="font-display font-bold text-sm">{r.player_name}</span>
                                                {r.position && (
                                                    <span className="font-mono text-[8px] uppercase border border-white/20 text-white/50 px-1 py-0.5">{r.position}</span>
                                                )}
                                                {r.known_player_key && (
                                                    <span className="font-mono text-[8px] uppercase bg-fa-mint/15 text-fa-mint px-1 py-0.5" title={s.inDb}>DB</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="flex items-center gap-1.5 font-mono text-[11px]">
                                                <span className={r.from_epl ? "text-white" : "text-white/45"}>{r.from_team}</span>
                                                <span className="text-fa-sky">→</span>
                                                <span className={r.to_epl ? "text-white" : "text-white/45"}>{r.to_team}</span>
                                                <span className={`ml-1 font-mono text-[8px] uppercase px-1 py-0.5 border flex items-center gap-1 ${st.cls}`}>
                                                    <Icon size={9} weight="fill" />
                                                    {r.direction === "out" ? s.outOfPl : (r.direction === "in" ? s.in : s.internal)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`font-mono text-[8px] uppercase px-1.5 py-0.5 ${r.transfer_type === "loan" ? "bg-orange-400/15 text-orange-300" : "bg-white/10 text-white/70"}`}>
                                                {r.transfer_type === "loan" ? s.loan : s.transfer}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums whitespace-nowrap">
                                            {fmtAmount(r.amount_eur) || r.fee_text || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1">
                                                {(r.sources || []).map((src) => (
                                                    <span key={src} className="font-mono text-[8px] uppercase border border-white/15 text-white/45 px-1 py-0.5">
                                                        {src === "sportmonks" ? "SM" : "BBC"}
                                                    </span>
                                                ))}
                                                {r.bbc_url && (
                                                    <a href={r.bbc_url} target="_blank" rel="noreferrer" className="text-fa-sky/70 hover:text-fa-sky"
                                                       data-testid={`transfers-bbc-link-${r.row_id}`}>
                                                        <ArrowSquareOut size={11} />
                                                    </a>
                                                )}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!running && filtered.length === 0 && (
                <div className="border border-white/10 bg-[#0d0d0d] p-6 font-mono text-[11px] text-white/50 text-center"
                     data-testid="transfers-empty">
                    {s.empty}
                </div>
            )}
        </div>
    );
}
